import React, { useRef, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import GalleryGrid from "./components/GalleryGrid";
import AboutMe from "./pages/AboutMe";
import Admin from "./pages/Admin";
import Login from "./pages/Login";
import Partners from "./pages/Partners";
import ContactForm from "./components/ContactForm";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import { fetchCategories, fetchAllPosts } from "./services/galleryService";
import defaultContent from "./data/content.json";
import "./App.css";

// Main site component with vertical scrolling
const MainSite = () => {
  const homeRef = useRef(null);
  const galleryRef = useRef(null);
  const aboutRef = useRef(null);
  const partnersRef = useRef(null);
  const contactRef = useRef(null);
  const containerRef = useRef(null);

  const [content, setContent] = useState({ gallery: { categories: [] } });
  const [loading, setLoading] = useState(true);


  // Fetch gallery data from Supabase on mount
  useEffect(() => {
    const loadGalleryData = async () => {
      setLoading(true);

      const { data: categoriesData, error: categoriesError } = await fetchCategories();

      if (categoriesError) {
        console.error('Failed to load categories:', categoriesError);
        // Fall back to default content
        setContent(defaultContent);
        setLoading(false);
        return;
      }

      // Fetch all posts
      const { data: postsData, error: postsError } = await fetchAllPosts();

      if (postsError) {
        console.error('Failed to load posts:', postsError);
        // Fall back to default content
        setContent(defaultContent);
        setLoading(false);
        return;
      }

      // Transform the data to match the expected structure
      const categories = categoriesData.map(category => ({
        id: category.id,
        name: category.name,
        posts: postsData
          .filter(post => post.category_id === category.id)
          .map(post => ({
            id: post.id,
            image: post.image_url,
            caption: post.caption,
            date: post.date,
          }))
          .sort((a, b) => a.display_order - b.display_order),
      }));

      setContent({
        gallery: {
          categories,
        },
      });

      setLoading(false);
    };

    loadGalleryData();
  }, []);

  const scrollToSection = (section) => {
    const refs = {
      home: homeRef,
      gallery: galleryRef,
      about: aboutRef,
      partners: partnersRef,
      contact: contactRef,

    };

    const targetRef = refs[section];
    if (targetRef && targetRef.current) {
      targetRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  };

  // Show loading state while fetching data
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#0a0a0a',
        color: '#ffffff',
        fontSize: '1.2rem',
      }}>
        Loading gallery...
      </div>
    );
  }

  return (
    <div className="app-container" ref={containerRef}>
      <Navbar scrollToSection={scrollToSection} />

      <section ref={homeRef} id="home" className="section">
        <Hero />
      </section>

      <section ref={galleryRef} id="gallery" className="section">
        <GalleryGrid galleries={content.gallery.categories} />
      </section>

      <section ref={aboutRef} id="about" className="section">
        <AboutMe />
      </section>

      <section ref={partnersRef} id="partners" className="section">
        <Partners />
      </section>

      <section ref={contactRef} id="contact" className="section">
        <ContactForm />
        <Footer scrollToSection={scrollToSection} />
      </section>
    </div>
  );
};

// Main App component with routing
const App = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<MainSite />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
